import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, request, setUnauthorizedHandler } from '../lib/api';
import { tokenStore, type ImpersonationState } from '../lib/token-store';
import type {
  AuthTokenResponse,
  ImpersonationToken,
  StopImpersonationResponse,
  UserProfile,
} from '../types/api';

export interface AuthContextValue {
  /** The identity requests are currently made as. */
  user: UserProfile | null;
  /** True once the initial "do we have a valid token?" check has finished. */
  ready: boolean;
  /** Set while acting as another user. */
  impersonation: ImpersonationState | null;
  isImpersonating: boolean;

  login: (email: string, password: string) => Promise<UserProfile>;
  logout: () => void;
  refreshUser: () => Promise<void>;

  startImpersonation: (userId: string, reason: string) => Promise<ImpersonationToken>;
  stopImpersonation: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [ready, setReady] = useState(false);
  const [impersonation, setImpersonation] = useState<ImpersonationState | null>(
    () => tokenStore.getImpersonation(),
  );
  const expiryTimer = useRef<number | undefined>(undefined);

  const clearSession = useCallback(() => {
    tokenStore.clear();
    setUser(null);
    setImpersonation(null);
  }, []);

  // One place decides what a 401 means: the token is gone, expired, or the
  // account was suspended mid-session. All three mean "sign in again".
  useEffect(() => {
    setUnauthorizedHandler(() => {
      // If an *impersonation* token expired, the admin's own session is
      // very likely still good — drop back to it instead of logging out.
      const adminToken = tokenStore.getAdminToken();
      if (adminToken) {
        tokenStore.setAccessToken(adminToken);
        tokenStore.setAdminToken(null);
        tokenStore.setImpersonation(null);
        setImpersonation(null);
        // Reload so every cached query refetches as the admin.
        window.location.reload();
        return;
      }
      clearSession();
    });
  }, [clearSession]);

  const refreshUser = useCallback(async () => {
    const token = tokenStore.getAccessToken();
    if (!token) {
      setUser(null);
      return;
    }
    try {
      setUser(await api.get<UserProfile>('/me'));
    } catch {
      // request() already routed a 401 through the handler above.
      setUser(null);
    }
  }, []);

  // Restore the session on boot.
  useEffect(() => {
    void refreshUser().finally(() => setReady(true));
  }, [refreshUser]);

  // An impersonation token cannot be refreshed, so when it lapses we return
  // the admin to their own identity rather than letting requests start
  // failing one by one. Keyed on the impersonation state alone: including
  // stopImpersonationLocal would re-arm the timer on every render and the
  // countdown would never fire.
  // biome-ignore lint/correctness/useExhaustiveDependencies: explained above
  useEffect(() => {
    window.clearTimeout(expiryTimer.current);
    if (!impersonation) return;

    const msLeft = impersonation.expiresAt - Date.now();
    if (msLeft <= 0) {
      void stopImpersonationLocal();
      return;
    }
    expiryTimer.current = window.setTimeout(() => {
      void stopImpersonationLocal();
    }, msLeft);

    return () => window.clearTimeout(expiryTimer.current);
  }, [impersonation]);

  /** Local-only fallback: put the admin token back without calling the API. */
  const stopImpersonationLocal = useCallback(async () => {
    const adminToken = tokenStore.getAdminToken();
    if (adminToken) tokenStore.setAccessToken(adminToken);
    tokenStore.setAdminToken(null);
    tokenStore.setImpersonation(null);
    setImpersonation(null);
    await refreshUser();
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    const res = await request<AuthTokenResponse>('/auth/login', {
      method: 'POST',
      body: { email, password },
      token: null,
    });
    tokenStore.clear();
    tokenStore.setAccessToken(res.accessToken);
    setUser(res.user);
    setImpersonation(null);
    return res.user;
  }, []);

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  const startImpersonation = useCallback(
    async (userId: string, reason: string) => {
      const adminToken = tokenStore.getAccessToken();
      const res = await api.post<ImpersonationToken>(
        `/admin/users/${userId}/impersonate`,
        { reason },
      );

      // Park the admin's own token before swapping it out, so stopping can
      // always get back even if the API call to stop fails.
      tokenStore.setAdminToken(adminToken);
      tokenStore.setAccessToken(res.accessToken);

      const state: ImpersonationState = {
        userId: res.actingAs.id,
        fullName: res.actingAs.fullName,
        email: res.actingAs.email,
        role: res.actingAs.role,
        expiresAt: Date.now() + res.expiresIn * 1000,
      };
      tokenStore.setImpersonation(state);
      setImpersonation(state);
      setUser(res.actingAs);
      return res;
    },
    [],
  );

  const stopImpersonation = useCallback(async () => {
    try {
      // Called with the impersonation token — that is what the API expects.
      const res = await api.post<StopImpersonationResponse>('/auth/impersonation/stop');
      tokenStore.setAccessToken(res.accessToken);
      tokenStore.setAdminToken(null);
      tokenStore.setImpersonation(null);
      setImpersonation(null);
      setUser(res.admin);
    } catch {
      // The impersonation token may already have expired; the parked admin
      // token is the fallback so support is never stranded.
      await stopImpersonationLocal();
    }
  }, [stopImpersonationLocal]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      ready,
      impersonation,
      isImpersonating: impersonation !== null,
      login,
      logout,
      refreshUser,
      startImpersonation,
      stopImpersonation,
    }),
    [user, ready, impersonation, login, logout, refreshUser, startImpersonation, stopImpersonation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
